/**
 * Open Food Facts API Integration
 * 100% Free, Open Data - No API key required!
 * API Documentation: https://world.openfoodfacts.org/data
 */

const OPEN_FOOD_FACTS_BASE = 'https://world.openfoodfacts.org/api/v0';

/**
 * Get product information by barcode
 * @param {string} barcode - UPC/EAN barcode
 * @returns {Promise<Object>} Product information
 */
export async function getProductByBarcode(barcode) {
  try {
    const response = await fetch(
      `${OPEN_FOOD_FACTS_BASE}/product/${barcode}.json`
    );
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.status === 0) {
      // Product not found
      return null;
    }
    
    return formatProductData(data.product);
  } catch (error) {
    console.error('Error fetching product by barcode:', error);
    return null;
  }
}

/**
 * Search for products by name
 * @param {string} searchTerm - Product name to search
 * @param {number} page - Page number (for pagination)
 * @returns {Promise<Array>} Array of products
 */
export async function searchProducts(searchTerm, page = 1) {
  try {
    const response = await fetch(
      `${OPEN_FOOD_FACTS_BASE}/cgi/search.pl?search_terms=${encodeURIComponent(searchTerm)}&page=${page}&page_size=20&json=true`
    );
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    
    const data = await response.json();
    return data.products?.map(formatProductData) || [];
  } catch (error) {
    console.error('Error searching products:', error);
    return [];
  }
}

/**
 * Get products by category
 * @param {string} category - Category name (e.g., "beverages", "snacks")
 * @param {number} page - Page number
 * @returns {Promise<Array>} Array of products in that category
 */
export async function getProductsByCategory(category, page = 1) {
  try {
    const response = await fetch(
      `https://world.openfoodfacts.org/category/${encodeURIComponent(category)}.json?page=${page}`
    );
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    
    const data = await response.json();
    return data.products?.map(formatProductData) || [];
  } catch (error) {
    console.error('Error getting products by category:', error);
    return [];
  }
}

/**
 * Format product data from Open Food Facts to your app's format
 * @param {Object} product - Raw product data from API
 * @returns {Object} Formatted product data
 */
function formatProductData(product) {
  if (!product) return null;
  
  return {
    barcode: product.code,
    name: product.product_name || product.product_name_en || 'Unknown Product',
    brand: product.brands || '',
    image: product.image_url || product.image_front_url || '',
    imageSmall: product.image_small_url || product.image_thumb_url || '',
    quantity: product.quantity || '',
    categories: product.categories_tags || [],
    ingredients: product.ingredients_text || '',
    nutritionGrade: product.nutrition_grades || '',
    nutrition: {
      energy: product.nutriments?.['energy-kcal_100g'] || 0,
      fat: product.nutriments?.fat_100g || 0,
      saturatedFat: product.nutriments?.['saturated-fat_100g'] || 0,
      carbohydrates: product.nutriments?.carbohydrates_100g || 0,
      sugars: product.nutriments?.sugars_100g || 0,
      fiber: product.nutriments?.fiber_100g || 0,
      proteins: product.nutriments?.proteins_100g || 0,
      salt: product.nutriments?.salt_100g || 0,
      sodium: product.nutriments?.sodium_100g || 0
    },
    estimatedPrice: estimatePrice(product),
    stores: product.stores || '',
    allergens: product.allergens_tags || [],
    labels: product.labels_tags || [],
    packaging: product.packaging || '',
    origin: product.origins || '',
    manufacturingPlaces: product.manufacturing_places || '',
    countries: product.countries || '',
    sourceUrl: `https://world.openfoodfacts.org/product/${product.code}`
  };
}

/**
 * Estimate price based on product data
 * Uses heuristics like category, brand, quantity
 * @param {Object} product - Product data
 * @returns {number} Estimated price in USD
 */
function estimatePrice(product) {
  // Base price estimation logic
  let basePrice = 3.99; // Default price
  
  // Adjust by category
  const categories = (product.categories || '').toLowerCase();
  if (categories.includes('beverages')) basePrice = 2.99;
  if (categories.includes('snacks')) basePrice = 3.49;
  if (categories.includes('dairy')) basePrice = 4.49;
  if (categories.includes('meat')) basePrice = 8.99;
  if (categories.includes('seafood')) basePrice = 12.99;
  if (categories.includes('frozen')) basePrice = 5.99;
  if (categories.includes('cereal')) basePrice = 4.99;
  if (categories.includes('condiments')) basePrice = 3.49;
  if (categories.includes('baby')) basePrice = 6.99;
  if (categories.includes('organic')) basePrice *= 1.3;
  
  // Adjust by quantity if available
  const quantity = product.quantity || '';
  const quantityMatch = quantity.match(/(\d+\.?\d*)\s*(g|kg|ml|l|oz|lb)/i);
  if (quantityMatch) {
    const amount = parseFloat(quantityMatch[1]);
    const unit = quantityMatch[2].toLowerCase();
    
    // Rough size adjustments
    if ((unit === 'g' && amount > 500) || (unit === 'kg' && amount > 0.5)) {
      basePrice *= 1.5;
    }
    if ((unit === 'ml' && amount > 1000) || (unit === 'l' && amount > 1)) {
      basePrice *= 1.3;
    }
  }
  
  // Adjust by brand recognition (premium brands)
  const brand = (product.brands || '').toLowerCase();
  const premiumBrands = ['organic', 'nature', 'whole', 'premium', 'gourmet'];
  if (premiumBrands.some(b => brand.includes(b))) {
    basePrice *= 1.2;
  }
  
  return parseFloat(basePrice.toFixed(2));
}

/**
 * Get product suggestions based on partial name
 * Useful for autocomplete
 * @param {string} partial - Partial product name
 * @returns {Promise<Array>} Array of product suggestions
 */
export async function getProductSuggestions(partial) {
  if (partial.length < 3) return [];
  
  try {
    const products = await searchProducts(partial);
    return products.slice(0, 5).map(p => ({
      name: p.name,
      brand: p.brand,
      image: p.imageSmall,
      barcode: p.barcode
    }));
  } catch (error) {
    console.error('Error getting product suggestions:', error);
    return [];
  }
}

/**
 * Get nutrition grade explanation
 * @param {string} grade - Nutrition grade (a, b, c, d, e)
 * @returns {Object} Grade explanation
 */
export function getNutritionGradeInfo(grade) {
  const grades = {
    a: { color: '#038141', label: 'Excellent', description: 'Very good nutritional quality' },
    b: { color: '#85BB2F', label: 'Good', description: 'Good nutritional quality' },
    c: { color: '#FECB02', label: 'Fair', description: 'Average nutritional quality' },
    d: { color: '#EE8100', label: 'Poor', description: 'Poor nutritional quality' },
    e: { color: '#E63E11', label: 'Bad', description: 'Bad nutritional quality' }
  };
  
  return grades[grade?.toLowerCase()] || { color: '#999', label: 'Unknown', description: 'No nutrition data' };
}

/**
 * Check if a product is likely available at a store
 * @param {Object} product - Product data
 * @param {string} storeName - Store name to check
 * @returns {boolean} True if likely available
 */
export function isAvailableAtStore(product, storeName) {
  if (!product || !storeName) return false;
  
  const stores = (product.stores || '').toLowerCase();
  return stores.includes(storeName.toLowerCase());
}

/**
 * Get popular products for a category
 * @param {string} category - Category slug
 * @returns {Promise<Array>} Popular products
 */
export async function getPopularProducts(category = 'all') {
  const categories = [
    'snacks',
    'beverages',
    'dairy',
    'breakfast',
    'fruits-and-vegetables',
    'meat',
    'fish',
    'pasta',
    'bread'
  ];
  
  const targetCategory = categories.includes(category) ? category : categories[0];
  return await getProductsByCategory(targetCategory, 1);
}












